import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Download, ShieldCheck, AlertCircle, Send } from 'lucide-react';

export const DownloadPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [supportStatus, setSupportStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const startDownload = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = 'excel-colour-picker.xlam';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('This download link is missing its purchase reference.');
      return;
    }

    const verifyPurchase = async () => {
      try {
        const response = await fetch('/.netlify/functions/verify-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Download verification failed');
        setDownloadUrl(data.downloadUrl);
        setEmail(data.customerEmail || '');
        setStatus('success');
        if (data.downloadUrl) startDownload(data.downloadUrl);
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
      }
    };

    verifyPurchase();
  }, [sessionId]);

  const submitSupportRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!sessionId) return;
    setSupportStatus('sending');
    try {
      const response = await fetch('/.netlify/functions/submit-download-support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, email, message, website }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not send your request');
      setSupportStatus('sent');
      setMessage('');
    } catch (err) {
      setSupportStatus('error');
      setError(err instanceof Error ? err.message : 'Could not send your request.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-xl mx-auto space-y-8 bg-white p-8 rounded-xl shadow-lg">
        {status === 'loading' && <p className="text-center text-gray-600">Verifying your purchase…</p>}

        {status === 'success' && (
          <>
            <div className="text-center">
              <ShieldCheck className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-3">Your download is ready</h1>
              <p className="text-gray-600 mb-6">It should begin automatically. If it does not, use this button.</p>
              <button
                onClick={() => downloadUrl && startDownload(downloadUrl)}
                className="w-full flex items-center justify-center bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700"
              >
                <Download className="mr-2 h-5 w-5" /> Download Again
              </button>
            </div>

            <div className="border-t pt-8">
              <h2 className="text-xl font-semibold mb-2">Still having trouble?</h2>
              <p className="text-sm text-gray-600 mb-5">Tell us what happened and provide the email where you would like our reply.</p>
              {supportStatus === 'sent' ? (
                <div className="bg-green-50 text-green-800 p-4 rounded-lg">Your support request was received.</div>
              ) : (
                <form onSubmit={submitSupportRequest} className="space-y-4">
                  <div>
                    <label htmlFor="support-email" className="block text-sm font-medium text-gray-700 mb-1">Reply email</label>
                    <input id="support-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label htmlFor="support-message" className="block text-sm font-medium text-gray-700 mb-1">What went wrong?</label>
                    <textarea id="support-message" required minLength={10} maxLength={2000} rows={5} value={message} onChange={(e) => setMessage(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
                  </div>
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input id="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
                  </div>
                  <button disabled={supportStatus === 'sending'} className="w-full flex items-center justify-center bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 disabled:opacity-60">
                    <Send className="mr-2 h-4 w-4" /> {supportStatus === 'sending' ? 'Sending…' : 'Send support request'}
                  </button>
                </form>
              )}
            </div>
            <Link to="/products/excel-colour-picker" className="text-green-600 hover:text-green-700 block text-center">Return to Product Page</Link>
          </>
        )}

        {status === 'error' && (
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-3">We could not verify this download</h1>
            <p className="text-red-700 mb-6">{error}</p>
            <Link to="/products/excel-colour-picker" className="text-green-600">Return to Product Page</Link>
          </div>
        )}
      </div>
    </div>
  );
};
